// Marketing — Broadcast schemas (Commitment 7).
//
// Broadcast is the PocketFlows parent concept covering two subtypes:
//   - CAMPAIGN: a scheduled one-off send (e.g. quarterly newsletter)
//   - TRIGGER:  an event-driven automation (e.g. donation_completed → email)
//
// Storage lives in `MarketingDataTable` keyed by `BROADCAST#<id>` (pk) +
// `META` (sk). GSI1 lists by org: `gsi1pk = ORG#<orgId>#BROADCASTS`,
// `gsi1sk = <createdAt ISO>#<id>` for deterministic pagination.
//
// Source of truth for the data: PocketFlows writes; we read.
// Reference: docs/initiatives/new/pocketflows/POCKETFLOW_OVERVIEW.md
// Reference: docs/reference/architecture/BACKEND_MONOREPO_ARCHITECTURE.md (M-1.1 Stage 2)

import { z } from 'zod';

// Branded ID schemas. Domain code re-exports these as branded TS types
// (BroadcastId, OrganizationId) so a raw `string` can't be used in place of
// a parsed ID by accident.
export const broadcastIdSchema = z.string().uuid({
  message: 'broadcastId must be a UUID',
});

export const organizationIdSchema = z
  .string()
  .min(1, { message: 'organizationId must be non-empty' })
  .max(256, { message: 'organizationId too long' });

// Broadcast subtypes — matches the PocketFlows `broadcast_type` column.
export const broadcastTypeSchema = z.enum(['CAMPAIGN', 'TRIGGER']);
export type BroadcastType = z.infer<typeof broadcastTypeSchema>;

// Broadcast lifecycle status. CAMPAIGN broadcasts walk through DRAFT →
// SCHEDULED → SENDING → SENT (or FAILED). TRIGGER broadcasts skip
// SCHEDULED and only fire on inbound events.
export const broadcastStatusSchema = z.enum([
  'DRAFT',
  'SCHEDULED',
  'SENDING',
  'SENT',
  'FAILED',
]);
export type BroadcastStatus = z.infer<typeof broadcastStatusSchema>;

// Full broadcast read model. ISO strings on the wire — parsing to Date
// happens at the consumer if needed.
export const broadcastSchema = z.object({
  id: broadcastIdSchema,
  organizationId: organizationIdSchema,
  broadcastType: broadcastTypeSchema,
  name: z.string().min(1).max(512),
  status: broadcastStatusSchema,
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type Broadcast = z.infer<typeof broadcastSchema>;

// List input. `cursor` is opaque (base64-encoded DDB LastEvaluatedKey).
// Limit is bounded to keep Lambda response payloads predictable.
export const broadcastListInputSchema = z.object({
  cursor: z.string().nullish(),
  limit: z.number().int().positive().max(100).default(50),
});
export type BroadcastListInput = z.infer<typeof broadcastListInputSchema>;

// List output. `nextCursor` is null when the page is the last one.
export const broadcastListOutputSchema = z.object({
  items: z.array(broadcastSchema),
  nextCursor: z.string().nullable(),
});
export type BroadcastListOutput = z.infer<typeof broadcastListOutputSchema>;
